import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import "modules/ChecklistsDashboard/components/AddChecklistModal/AddChecklistModal.scss";
import { useCreateChecklistMutation } from "modules/ChecklistsDashboard/components/AddChecklistModal/hooks";

import type { Checklist, ChecklistID } from "models";

type AddChecklistFormValues = Pick<Checklist, "name">;

type AddChecklistModalProps = {
  isOpen: boolean;
  isDealerChecklist: boolean;
  onClose: () => void;
  onCreate: (checklistID: ChecklistID) => void;
};

const FormHelper = createFormHelper<AddChecklistFormValues>();

export const AddChecklistModal = ({ isOpen, isDealerChecklist, onClose, onCreate }: AddChecklistModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);

  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: createChecklist, isPending } = useCreateChecklistMutation();

  const handleClose = () => {
    setHasErrors(false);
    onClose();
  };

  const handleSubmit = async ({ name }: AddChecklistFormValues) => {
    if (!name.trim()) {
      setHasErrors(true);
      return;
    }

    const checklistID = await createChecklist({ name: name.trim(), dealer_id: isDealerChecklist ? selectedDealer?.id : undefined });
    setHasErrors(false);
    onCreate(checklistID);
  };

  return (
    <Modal size="tiny" className="AddChecklistModal" open={isOpen} onClose={handleClose} closeOnDimmerClick={false}>
      <Modal.Header>
        {isDealerChecklist ? t("v8_add_new_dealer_checklist", "Add new dealer checklist") : t("v8_add_new_default_checklist", "Add new default checklist")}
      </Modal.Header>

      <FormHelper.Form onSubmit={handleSubmit} defaultValues={{ name: "" }}>
        <Modal.Content>
          <Form.Field required error={hasErrors}>
            <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_checklist_name", "Checklist name"), fluid: true }} />
          </Form.Field>
        </Modal.Content>

        <Modal.Actions>
          <Button type="button" className="light" onClick={handleClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>

          <Button type="submit" color="green" disabled={isPending} loading={isPending}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </Modal.Actions>
      </FormHelper.Form>
    </Modal>
  );
};
